import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../ApiContext'; 
import toast from 'react-hot-toast'; 
import './ApkManager.css'; 

const APK_ENDPOINT = '/apks';

const ApkManager = ({ onSelectApk, onClose }) => { 
    const { api } = useApi();
    
    // --- State for Data & CRUD operations ---
    const [apks, setApks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    
    // --- State for Upload Form ---
    const [file, setFile] = useState(null);
    const [version, setVersion] = useState('');
    const [description, setDescription] = useState('');

    // --- Fetch APK Versions (READ) ---
    const fetchApkVersions = useCallback(async () => {
        if (!api) return;
        setLoading(true);
        try {
            const response = await api.get(APK_ENDPOINT); 
            setApks(response.data);
        } catch (err) {
            console.error('Error fetching APKs:', err.response || err);
            toast.error('Failed to load APK versions from the server.');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchApkVersions();
    }, [fetchApkVersions]);

    // --- Upload Handlers (CREATE) ---
    const handleFileChange = (event) => {
        setFile(event.target.files[0]);
    };

    const handleUpload = async (event) => {
        event.preventDefault();
        
        if (!file || !version || !api) {
            toast.error('Version and APK file are required.');
            return;
        }

        const formData = new FormData();
        formData.append('apkFile', file);
        formData.append('version', version);
        formData.append('description', description); 

        setUploading(true);
        setUploadProgress(0);

        try {
            await api.post(`${APK_ENDPOINT}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const total = progressEvent.total || file.size;
                    const current = progressEvent.loaded;
                    const percentCompleted = Math.round((current * 100) / total);
                    setUploadProgress(percentCompleted);
                }
            });

            toast.success(`APK v${version} uploaded successfully!`);
            
            setFile(null);
            setVersion('');
            setDescription('');
            document.getElementById('apk-file-input').value = '';
            
            await fetchApkVersions(); 
        } catch (err) {
            console.error('Error uploading APK:', err.response || err);
            const errorMessage = err.response?.data?.message || err.message || 'An unknown error occurred.';
            toast.error(`Upload failed: ${errorMessage}`);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // --- Delete Handler (DELETE) ---
    const handleDelete = async (event, id, version) => {
        event.stopPropagation(); 
        
        if (!api || !window.confirm(`Are you sure you want to delete APK version ${version}?`)) {
            return;
        }

        try {
            await api.delete(`${APK_ENDPOINT}/${id}`); 
            toast.success(`APK v${version} deleted.`);
            setApks(prevApks => prevApks.filter(apk => apk.id !== id));
        } catch (err) {
            console.error('Error deleting APK:', err.response || err);
            const errorMessage = err.response?.data?.message || err.message || 'An unknown error occurred.';
            toast.error(`Deletion failed: ${errorMessage}`);
        }
    };
    
    // --- CORE SELECTION LOGIC ---
    const handleApkSelection = (apk) => {
        onSelectApk(apk.file_path, apk.version);
        onClose(); 
    };

    // --- APK Card Component ---
    const ApkItemCard = ({ apk }) => (
        <div className="apk-card selectable-card" onClick={() => handleApkSelection(apk)}>
            <button 
                className="delete-icon-button" 
                onClick={(e) => handleDelete(e, apk.id, apk.version)}
                title={`Delete v${apk.version}`}
            >
                &times;
            </button>
            <div className="apk-card-header">
                <span className="apk-version">v{apk.version}</span>
                <span className="apk-date">{new Date(apk.upload_time).toLocaleDateString()}</span>
            </div>
            <p className="apk-file-name" title={apk.file_name}>File: <strong>{apk.file_name}</strong></p>
            <p className="apk-description">{apk.description || 'No description provided.'}</p>
        </div>
    );

    return (
        <div className="modal-overlay">
            <div className="modal-content large-modal apk-manager-modal">
                <button type="button" className="close-button" onClick={onClose}>&times;</button> 
                <h2 className="list-title">APK Management & Selection</h2>
                
                <form onSubmit={handleUpload} className="upload-apk-form-card">
                    <h3 className="upload-form-title">Upload New APK</h3>
                    <div className="upload-input-group">
                        <div className="file-select-area">
                            <label htmlFor="apk-file-input" className="file-label">
                                {file ? `Selected: ${file.name}` : 'Click to select APK file (.apk)'}
                            </label>
                            <input
                                type="file"
                                id="apk-file-input"
                                accept=".apk"
                                onChange={handleFileChange}
                                required
                                style={{ display: 'none' }}
                            />
                        </div>
                        <input
                            type="text"
                            className="text-input"
                            placeholder="Version (e.g., 1.0.1)"
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                            required
                        />
                        <textarea
                            className="text-input description-input"
                            placeholder="Description (Optional)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>
                    <button 
                        type="submit" 
                        className={`upload-button ${uploading ? 'button-uploading' : ''}`} 
                        disabled={uploading || !file || !version}
                        style={{
                            background: uploading 
                                ? `linear-gradient(to right, #22c55e ${uploadProgress}%, #3b82f6 ${uploadProgress}%)` 
                                : undefined
                        }}
                    >
                        {uploading ? `Uploading (${uploadProgress}%)` : 'Upload APK'}
                    </button>
                </form>

                <hr style={{ margin: '20px 0' }}/>
                
                <h3 className="list-title">Available Versions ({apks.length})</h3>
                <p className="item-note">Click any card to select the APK and autofill the form.</p>
                
                <div className="apk-selection-grid">
                    {loading ? (
                        <p className="loading-message">Loading APKs...</p>
                    ) : apks.length === 0 ? (
                        <p className="empty-message">No APKs uploaded.</p>
                    ) : (
                        apks.map(apk => <ApkItemCard key={apk.id} apk={apk} />)
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApkManager;